<template>
  <div class="product-detail-container">
    <!-- 现代化页面头部 -->
    <div class="page-header">
      <div class="header-content">
        <div class="header-left">
          <div class="breadcrumb">
            <el-button 
              type="text" 
              icon="el-icon-arrow-left" 
              @click="$router.go(-1)"
              class="back-btn"
            >
              返回商品列表
            </el-button>
          </div>
          <h1 class="page-title">
            <i class="el-icon-goods"></i>
            商品详情
          </h1>
          <p class="page-subtitle" v-if="productDetail">{{ productDetail.product_name }}</p>
        </div>
        <div class="header-actions">
          <el-button size="medium" @click="showPriceDialog" class="action-btn">
            <i class="el-icon-price-tag"></i> 价格设置
          </el-button>
          <el-button type="primary" size="medium" @click="handleEdit" class="action-btn">
            <i class="el-icon-edit"></i> 编辑商品
          </el-button>
        </div>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="main-content">
      <!-- 统计卡片 -->
      <div v-if="productDetail && !loading" class="stats-section">
        <el-row :gutter="20">
          <el-col :span="6">
            <div class="stat-card">
              <div class="stat-icon price">
                <i class="el-icon-price-tag"></i>
              </div>
              <div class="stat-content">
                <div class="stat-number">¥{{ getBasePrice() }}</div>
                <div class="stat-label">基础价格</div>
              </div>
            </div>
          </el-col>
          <el-col :span="6">
            <div class="stat-card">
              <div class="stat-icon featured">
                <i class="el-icon-star-on"></i>
              </div>
              <div class="stat-content">
                <div class="stat-number" :class="{ 'featured': productDetail.is_featured }">
                  {{ productDetail.is_featured ? '推荐' : '普通' }}
                </div>
                <div class="stat-label">推荐状态</div>
              </div>
            </div>
          </el-col>
          <el-col :span="6">
            <div class="stat-card">
              <div class="stat-icon sales">
                <i class="el-icon-s-data"></i>
              </div>
              <div class="stat-content">
                <div class="stat-number">{{ productDetail.sales_quantity || 0 }}</div>
                <div class="stat-label">销售数量</div>
              </div>
            </div>
          </el-col>
          <el-col :span="6">
            <div class="stat-card">
              <div class="stat-icon status">
                <i class="el-icon-success"></i>
              </div>
              <div class="stat-content">
                <div class="stat-number" :class="getActiveStatus() ? 'active' : 'inactive'">
                  {{ getActiveStatus() ? '上架' : '下架' }}
                </div>
                <div class="stat-label">商品状态</div>
              </div>
            </div>
          </el-col>
        </el-row>
      </div>

      <div v-loading="loading" class="content">
        <el-row :gutter="24" v-if="productDetail">
          <!-- 左侧：商品基本信息 -->
          <el-col :span="16">
          <el-card title="基本信息" class="info-card main-info-card">
            <div class="product-info">
              <div class="product-images">
                <el-image
                  :src="productDetail.main_image"
                  :preview-src-list="allImages"
                  class="main-image"
                  fit="cover"
                >
                  <div slot="error" class="image-error">
                    <i class="el-icon-picture-outline"></i>
                  </div>
                </el-image>
                <div class="detail-images" v-if="productDetail.detail_images && productDetail.detail_images.length">
                  <el-image
                    v-for="(img, index) in productDetail.detail_images.slice(0, 4)"
                    :key="index"
                    :src="img"
                    :preview-src-list="allImages"
                    class="detail-image"
                    fit="cover"
                  />
                </div>
              </div>

              <div class="product-details">
                <h3>{{ productDetail.product_name }}</h3>
                <div class="detail-row">
                  <label>商品编码：</label>
                  <span>{{ productDetail.product_code }}</span>
                </div>
                <div class="detail-row">
                  <label>商品分类：</label>
                  <span>{{ productDetail.category || '未分类' }}</span>
                </div>
                <div class="detail-row">
                  <label>销售单位：</label>
                  <span>{{ productDetail.unit }}</span>
                </div>
                <div class="detail-row">
                  <label>基础价格：</label>
                  <span class="price">￥{{ productDetail.base_price }}</span>
                </div>
                <div class="detail-row">
                  <label>当前状态：</label>
                  <el-tag :type="getActiveStatus() ? 'success' : 'danger'">
                    {{ getActiveStatus() ? '上架' : '下架' }}
                  </el-tag>
                  <el-tag v-if="getFeaturedStatus()" type="primary" size="small">推荐</el-tag>
                </div>
                <div class="detail-row">
                  <label>商品描述：</label>
                  <p class="description">{{ productDetail.description || '暂无描述' }}</p>
                </div>
              </div>
            </div>
          </el-card>

          <!-- 规格参数 -->
          <el-card title="规格参数" class="info-card spec-card" v-if="productDetail.specifications">
            <div class="specifications">
              <div 
                v-for="(value, key) in productDetail.specifications" 
                :key="key"
                class="spec-item"
              >
                <label>{{ key }}：</label>
                <span>{{ value }}</span>
              </div>
            </div>
          </el-card>
        </el-col>

        <!-- 右侧：库存和销售数据 -->
        <el-col :span="8">
          <div class="right-cards-wrapper">
            <el-card title="库存信息" class="info-card side-card">
              <div class="stock-info">
                <div class="stock-item">
                  <div class="stock-label">当前库存</div>
                  <div class="stock-value" :class="{'low-stock': productDetail.is_low_stock}">
                    {{ productDetail.stock_quantity }}{{ productDetail.unit }}
                  </div>
                </div>
                <div class="stock-item">
                  <div class="stock-label">销售数量</div>
                  <div class="stock-value">{{ productDetail.sales_quantity }}{{ productDetail.unit }}</div>
                </div>
                <div class="stock-item">
                  <div class="stock-label">最低库存</div>
                  <div class="stock-value">{{ productDetail.min_stock }}{{ productDetail.unit }}</div>
                </div>
                <div class="stock-item" v-if="productDetail.is_low_stock">
                  <el-alert
                    title="库存预警"
                    description="当前库存低于最低库存，请及时补充"
                    type="warning"
                    :closable="false"
                  />
                </div>
              </div>
            </el-card>

            <el-card title="时间信息" class="info-card side-card">
              <div class="time-info">
                <div class="time-item">
                  <label>创建时间：</label>
                  <span>{{ formatDate(productDetail.created_at) }}</span>
                </div>
                <div class="time-item">
                  <label>更新时间：</label>
                  <span>{{ formatDate(productDetail.updated_at) }}</span>
                </div>
                <div class="time-item">
                  <label>排序序号：</label>
                  <span>{{ productDetail.sort_order }}</span>
                </div>
              </div>
            </el-card>
          </div>
        </el-col>
      </el-row>

      <!-- 富文本描述 - 全宽显示 -->
      <el-card v-if="hasRichContent" title="详细描述" class="info-card rich-content-card full-width-card">
        <div class="rich-content-wrapper">
          <div 
            class="rich-content" 
            v-html="productDetail.rich_description"
            ref="richContent"
          ></div>
        </div>
      </el-card>

      <!-- 会员价格设置 -->
      <el-card title="会员价格" class="price-card" v-if="productDetail && productDetail.membership_prices">
        <el-table :data="productDetail.membership_prices" stripe>
          <el-table-column prop="level_name" label="会员等级" />
          <el-table-column label="会员价格">
            <template slot-scope="scope">
              <span class="price">￥{{ scope.row.price }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="min_quantity" label="最小购买数量">
            <template slot-scope="scope">
              {{ scope.row.min_quantity }}{{ productDetail.unit }}
            </template>
          </el-table-column>
          <el-table-column label="折扣率">
            <template slot-scope="scope">
              {{ ((scope.row.price / productDetail.base_price) * 100).toFixed(1) }}%
            </template>
          </el-table-column>
        </el-table>
      </el-card>
    </div>



    <!-- 价格设置对话框 -->
    <el-dialog title="会员价格设置" :visible.sync="priceDialogVisible" width="600px">
      <div class="price-settings">
        <div class="base-price-info">
          <label>基础价格：</label>
          <span class="price">￥{{ productDetail ? productDetail.base_price : 0 }}</span>
        </div>
        <el-table :data="membershipPrices" stripe>
          <el-table-column prop="level_name" label="会员等级" width="150" />
          <el-table-column label="会员价格" width="150">
            <template slot-scope="scope">
              <el-input-number
                v-model="scope.row.price"
                :precision="2"
                :min="0"
                :max="9999999.99"
                size="small"
              />
            </template>
          </el-table-column>
          <el-table-column label="最小数量" width="120">
            <template slot-scope="scope">
              <el-input-number
                v-model="scope.row.min_quantity"
                :min="1"
                size="small"
              />
            </template>
          </el-table-column>
          <el-table-column label="折扣率">
            <template slot-scope="scope">
              {{ productDetail ? ((scope.row.price / productDetail.base_price) * 100).toFixed(1) : 0 }}%
            </template>
          </el-table-column>
        </el-table>
      </div>
      <div slot="footer">
        <el-button @click="priceDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handlePriceUpdate">保存</el-button>
      </div>
    </el-dialog>
    </div>
  </div>
</template>

<script>
import { getProduct, updateMembershipPrices } from '@/api/product'
import { processImageUrl } from '@/utils/config'

export default {
  name: 'ProductDetail',
  data() {
    return {
      loading: false,
      productDetail: null,
      priceDialogVisible: false,
      membershipPrices: [],
      resizeTimer: null
    }
  },
  
  computed: {
    allImages() {
      if (!this.productDetail) return []
      const images = [this.productDetail.main_image]
      if (this.productDetail.detail_images) {
        images.push(...this.productDetail.detail_images)
      }
      return images.filter(img => img)
    },
    
    // 检查是否有有效的富文本内容
    hasRichContent() {
      if (!this.productDetail || !this.productDetail.rich_description) return false
      
      // 移除HTML标签并去除空白字符，检查是否有实际内容
      const textContent = this.productDetail.rich_description.replace(/<[^>]*>/g, '').trim()
      return textContent.length > 0
    }
  },
  
  created() {
    this.fetchData()
  },
  
  mounted() {
    // 调整富文本中的图片样式和卡片高度
    this.$nextTick(() => {
      this.adjustRichContentImages()
      this.adjustCardHeights()
    })
    
    // 监听窗口大小变化
    window.addEventListener('resize', this.handleResize)
  },
  
  beforeDestroy() {
    // 清理事件监听器
    window.removeEventListener('resize', this.handleResize)
  },
  
  updated() {
    // 内容更新时重新调整图片样式和卡片高度
    this.$nextTick(() => {
      this.adjustRichContentImages()
      this.adjustCardHeights()
    })
  },
  
  methods: {
    async fetchData() {
      const productId = this.$route.params.id
      if (!productId) {
        this.$message.error('商品ID不能为空')
        this.$router.go(-1)
        return
      }
      
      this.loading = true
      try {
        const response = await getProduct(productId)
        const product = response.data
        
        
        // 处理图片URL
        this.productDetail = {
          ...product,
          main_image: processImageUrl(product.main_image),
          detail_images: (product.detail_images || []).map(url => processImageUrl(url))
        }
        
        this.initMembershipPrices()
        
        // 等待DOM更新后调整图片样式和布局
        setTimeout(() => {
          this.adjustRichContentImages()
          this.adjustCardHeights()
        }, 300)
      } catch (error) {
        this.$message.error('获取商品详情失败')
        this.$router.go(-1)
      } finally {
        this.loading = false
      }
    },
    
    initMembershipPrices() {
      if (this.productDetail && this.productDetail.membership_prices) {
        this.membershipPrices = this.productDetail.membership_prices.map(item => ({
          membership_level_id: item.level_id,
          level_name: item.level_name,
          price: item.price,
          min_quantity: item.min_quantity
        }))
      }
    },
    
    handleEdit() {
      this.$router.push(`/admin/products/${this.$route.params.id}/edit`)
    },
    
    showPriceDialog() {
      this.initMembershipPrices()
      this.priceDialogVisible = true
    },
    
    async handlePriceUpdate() {
      try {
        const prices = this.membershipPrices.map(item => ({
          membership_level_id: item.membership_level_id,
          price: item.price,
          min_quantity: item.min_quantity
        }))
        
        await updateMembershipPrices(this.$route.params.id, { prices })
        this.$message.success('会员价格更新成功')
        this.priceDialogVisible = false
        this.fetchData() // 重新获取数据
      } catch (error) {
        this.$message.error('价格更新失败')
      }
    },
    
    formatDate(date) {
      return new Date(date).toLocaleString('zh-CN')
    },
    
    // 获取基础价格
    getBasePrice() {
      if (!this.productDetail) return '0.00'
      if (this.productDetail.prices && this.productDetail.prices.base_price) {
        return parseFloat(this.productDetail.prices.base_price).toFixed(2)
      }
      return parseFloat(this.productDetail.base_price || 0).toFixed(2)
    },
    
    // 获取商品上架状态（兼容不同字段名）
    getActiveStatus() {
      if (!this.productDetail) return false
      const product = this.productDetail
      if (product.is_active !== undefined) return !!product.is_active
      if (product.active !== undefined) return !!product.active
      if (product.status !== undefined) return product.status === 1 || product.status === 'active'
      if (product.state !== undefined) return product.state === 1 || product.state === 'active'
      return false
    },
    
    // 获取推荐状态（兼容不同字段名）
    getFeaturedStatus() {
      if (!this.productDetail) return false
      const product = this.productDetail
      if (product.is_featured !== undefined) return !!product.is_featured
      if (product.featured !== undefined) return !!product.featured
      if (product.recommend !== undefined) return !!product.recommend
      if (product.is_recommend !== undefined) return !!product.is_recommend
      return false
    },
    
    // 调整富文本内容中的图片样式
    adjustRichContentImages() {
      if (!this.$refs.richContent) return
      
      const images = this.$refs.richContent.querySelectorAll('img')
      images.forEach(img => {
        // 确保图片完全显示
        img.style.maxWidth = '100%'
        img.style.width = 'auto'
        img.style.height = 'auto'
        img.style.display = 'block'
        img.style.margin = '16px auto'
        img.style.borderRadius = '8px'
        img.style.objectFit = 'contain'
        img.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)'
        img.style.transition = 'all 0.3s ease'
        
        // 添加点击放大功能
        img.style.cursor = 'pointer'
        img.onclick = () => {
          this.previewImage(img.src)
        }
        
        // 处理图片加载错误
        img.onerror = () => {
          img.style.display = 'none'
          console.error('图片加载失败:', img.src)
        }
      })
    },
    
    // 图片预览功能
    previewImage(src) {
      // 使用Element UI的图片预览功能
      const h = this.$createElement
      
      this.$msgbox({
        title: '图片预览',
        message: h('div', {
          style: {
            textAlign: 'center',
            padding: '20px'
          }
        }, [
          h('img', {
            attrs: { src },
            style: {
              maxWidth: '100%',
              maxHeight: '60vh',
              objectFit: 'contain',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
            }
          })
        ]),
        showCancelButton: false,
        confirmButtonText: '关闭',
                 customClass: 'image-preview-dialog'
       })
     },
     
          // 调整卡片高度
     adjustCardHeights() {
       this.$nextTick(() => {
         if (window.innerWidth <= 768) return // 移动端不调整
         
         setTimeout(() => {
           const leftCol = document.querySelector('.content .el-row .el-col:first-child')
           const rightCol = document.querySelector('.content .el-row .el-col:last-child')
           const rightWrapper = document.querySelector('.right-cards-wrapper')
           
           if (leftCol && rightCol && rightWrapper) {
             // 重置高度限制
             rightWrapper.style.height = 'auto'
             rightWrapper.style.minHeight = 'auto'
             
             // 等待重置完成后计算高度
             this.$nextTick(() => {
               const leftHeight = leftCol.offsetHeight
               const rightHeight = rightCol.offsetHeight
               
                               // 计算高度差异
                const heightDiff = Math.abs(leftHeight - rightHeight)
                
                // 只有高度差异超过10px时才调整
                if (heightDiff > 10) {
                  if (leftHeight > rightHeight) {
                    // 左侧更高，调整右侧
                    rightWrapper.style.height = `${leftHeight}px`
                  } else {
                    // 右侧更高，调整左侧主信息卡片
                    const mainCard = document.querySelector('.main-info-card')
                    if (mainCard) {
                      const specCard = document.querySelector('.spec-card')
                      const specHeight = specCard ? specCard.offsetHeight + 24 : 0
                      
                      const targetMainHeight = rightHeight - specHeight
                      if (targetMainHeight > 300) { // 确保最小高度
                        mainCard.style.height = `${targetMainHeight}px`
                      }
                    }
                  }
                }
               
               console.log('高度调整:', {
                 leftHeight,
                 rightHeight,
                 heightDiff,
                 adjustment: heightDiff <= 10 ? 'no adjustment needed' : 
                           leftHeight > rightHeight ? 'right side adjusted' : 'left side adjusted'
               })
             })
           }
         }, 150)
       })
     },
      
      // 处理窗口大小变化
      handleResize() {
        // 防抖处理
        clearTimeout(this.resizeTimer)
        this.resizeTimer = setTimeout(() => {
          // 重置所有高度设置
          const rightWrapper = document.querySelector('.right-cards-wrapper')
          const mainCard = document.querySelector('.main-info-card')
          
          if (rightWrapper) {
            rightWrapper.style.height = 'auto'
            rightWrapper.style.minHeight = window.innerWidth > 768 ? '550px' : 'auto'
          }
          
          if (mainCard) {
            mainCard.style.height = 'auto'
            mainCard.style.minHeight = window.innerWidth > 768 ? '450px' : 'auto'
          }
          
          // 重新调整高度
          this.adjustCardHeights()
        }, 200)
      }
    }
}
</script>

<style scoped>
.product-detail-container {
  min-height: 100vh;
  background: #f5f7fa;
  animation: fadeInUp 0.6s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 页面头部 */
.page-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 30px 40px;
  margin-bottom: 30px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1400px;
  margin: 0 auto;
}

.header-left {
  flex: 1;
}

.breadcrumb {
  margin-bottom: 10px;
}

.back-btn {
  color: rgba(255, 255, 255, 0.8) !important;
  font-size: 14px;
  padding: 0;
  border: none;
  background: none !important;
}

.back-btn:hover {
  color: white !important;
}

.page-title {
  color: white;
  font-size: 28px;
  font-weight: 600;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 12px;
}

.page-title i {
  font-size: 32px;
}

.page-subtitle {
  color: rgba(255, 255, 255, 0.8);
  font-size: 16px;
  margin: 0;
  font-weight: 400;
}

.header-actions {
  display: flex;
  gap: 12px;
  align-items: center;
}

.action-btn {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}

.action-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
}

/* 主要内容区域 */
.main-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 40px 40px;
}

/* 统计卡片 */
.stats-section {
  margin-bottom: 30px;
}

.stat-card {
  background: white;
  border-radius: 20px;
  padding: 24px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid #f1f3f4;
  position: relative;
  overflow: hidden;
}

.stat-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.12);
}

.stat-icon {
  width: 60px;
  height: 60px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  background: white;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  position: relative;
}

.stat-icon::before {
  content: '';
  position: absolute;
  inset: -2px;
  border-radius: 22px;
  padding: 2px;
  background: linear-gradient(135deg, #4facfe, #00f2fe);
  mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  mask-composite: exclude;
  -webkit-mask-composite: xor;
}

.stat-icon i {
  color: #4facfe;
  z-index: 1;
}

.stat-icon.price::before {
  background: linear-gradient(135deg, #667eea, #764ba2);
}

.stat-icon.price i {
  color: #667eea;
}

.stat-icon.stock::before {
  background: linear-gradient(135deg, #ffeaa7, #fdcb6e);
}

.stat-icon.stock i {
  color: #fdcb6e;
}

.stat-icon.sales::before {
  background: linear-gradient(135deg, #fd79a8, #e84393);
}

.stat-icon.sales i {
  color: #fd79a8;
}

.stat-icon.status::before {
  background: linear-gradient(135deg, #67c23a, #85ce61);
}

.stat-icon.status i {
  color: #67c23a;
}

.stat-content {
  flex: 1;
}

.stat-number {
  font-size: 28px;
  font-weight: 700;
  color: #303133;
  line-height: 1;
  margin-bottom: 4px;
}

.stat-number.low-stock {
  color: #f56c6c;
}

.stat-number.active {
  color: #67c23a;
}

.stat-number.inactive {
  color: #f56c6c;
}

.stat-label {
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

/* 内容区域 */
.content {
  background: transparent;
}

.info-card {
  margin-bottom: 24px;
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border-radius: 16px;
  overflow: hidden;
}

.product-info {
  display: flex;
  gap: 30px;
  padding: 20px;
  flex: 1;
  align-items: flex-start;
}

.product-images {
  flex: 0 0 320px;
}

.main-image {
  width: 320px;
  height: 320px;
  border-radius: 16px;
  margin-bottom: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  transition: all 0.3s ease;
}

.main-image:hover {
  transform: scale(1.02);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.16);
}

.detail-images {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.detail-image {
  width: 75px;
  height: 75px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.detail-image:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.image-error {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background: #f5f7fa;
  color: #909399;
  font-size: 50px;
  border-radius: 16px;
}

.product-details {
  flex: 1;
}

.product-details h3 {
  font-size: 28px;
  color: #303133;
  margin-bottom: 24px;
  font-weight: 600;
  line-height: 1.3;
}

.detail-row {
  margin-bottom: 16px;
  display: flex;
  align-items: flex-start;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 12px;
  border-left: 4px solid #409eff;
}

.detail-row label {
  font-weight: 600;
  color: #606266;
  width: 120px;
  flex-shrink: 0;
  font-size: 14px;
}

.detail-row span {
  color: #303133;
  flex: 1;
  font-size: 14px;
}

.detail-row .price {
  color: #e6a23c;
  font-weight: 600;
  font-size: 18px;
}

.detail-row .description {
  margin: 0;
  line-height: 1.6;
  color: #606266;
}

.specifications {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  padding: 20px;
}

.spec-item {
  display: flex;
  align-items: center;
  padding: 16px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 12px;
  border-left: 4px solid #28a745;
  transition: all 0.3s ease;
}

.spec-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.spec-item label {
  font-weight: 600;
  color: #606266;
  width: 120px;
  flex-shrink: 0;
  font-size: 14px;
}

.spec-item span {
  color: #303133;
  font-weight: 500;
}

.stock-info {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  flex: 1;
}

.stock-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 12px;
  border-left: 4px solid #17a2b8;
  transition: all 0.3s ease;
}

.stock-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.stock-item:last-child {
  margin-bottom: 0;
}

.stock-label {
  font-weight: 600;
  color: #606266;
  font-size: 14px;
}

.stock-value {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.stock-value.low-stock {
  color: #f56c6c !important;
}

.time-info {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  flex: 1;
}

.time-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 12px;
  border-left: 4px solid #6f42c1;
  transition: all 0.3s ease;
}

.time-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.time-item:last-child {
  margin-bottom: 0;
}

.time-item label {
  font-weight: 600;
  color: #606266;
  font-size: 14px;
}

.time-item span {
  color: #303133;
  font-weight: 500;
}

.price-card {
  margin-top: 24px;
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border-radius: 16px;
  overflow: hidden;
}

/* 确保富文本卡片和价格卡片样式一致 */
.full-width-card,
.price-card {
  width: 100%;
  box-sizing: border-box;
}

/* 全宽富文本卡片特殊样式 */
.full-width-card .rich-content img {
  max-width: 100% !important;
  width: auto !important;
  height: auto !important;
  display: block !important;
  margin: 16px auto !important;
  object-fit: contain !important;
}

.current-stock {
  font-size: 16px;
  font-weight: 600;
  color: #409eff;
}

.price-settings {
  margin-bottom: 20px;
}

.base-price-info {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  padding: 16px;
  background: linear-gradient(135deg, #e6f3ff 0%, #cce5ff 100%);
  border-radius: 12px;
  border-left: 4px solid #409eff;
}

.base-price-info label {
  font-weight: 600;
  color: #606266;
  margin-right: 12px;
  font-size: 14px;
}

.base-price-info .price {
  color: #409eff;
  font-size: 18px;
  font-weight: 600;
}

/* 富文本内容样式 */
.rich-content-card {
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border-radius: 16px;
  overflow: hidden;
}

.full-width-card {
  margin-top: 24px;
}

/* 右侧卡片容器 */
.right-cards-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 24px;
  min-height: 550px; /* 初始最小高度 */
}

.side-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.side-card :deep(.el-card__body) {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

/* 确保左右两侧卡片高度一致 */
.main-info-card {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 450px; /* 初始最小高度 */
}

.spec-card {
  margin-top: 24px;
  flex-shrink: 0;
}

/* 左侧列容器 */
.content .el-row .el-col:first-child {
  display: flex;
  flex-direction: column;
}

/* 右侧列容器 */
.content .el-row .el-col:last-child {
  display: flex;
  flex-direction: column;
}

/* 确保两侧高度一致的关键样式 */
.content .el-row {
  align-items: flex-start;
}

.content .el-row .el-col {
  display: flex;
  flex-direction: column;
}

/* 左侧主信息卡片样式 */
.main-info-card :deep(.el-card__body) {
  padding: 0;
}

/* 右侧卡片样式调整 */
.side-card:first-child {
  margin-bottom: 0;
}

.rich-content-wrapper {
  padding: 20px;
  overflow: hidden;
}

.rich-content {
  line-height: 1.8;
  color: #333;
  font-size: 14px;
  word-wrap: break-word;
  overflow-wrap: break-word;
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
}

/* 富文本内容中的元素样式 */
.rich-content h1,
.rich-content h2,
.rich-content h3,
.rich-content h4,
.rich-content h5,
.rich-content h6 {
  margin: 20px 0 12px 0;
  padding: 0;
  font-weight: 600;
  line-height: 1.4;
}

.rich-content h1 {
  font-size: 24px;
  color: #303133;
  border-bottom: 3px solid #409eff;
  padding-bottom: 8px;
}

.rich-content h2 {
  font-size: 20px;
  color: #409eff;
  border-left: 4px solid #409eff;
  padding-left: 12px;
}

.rich-content h3 {
  font-size: 18px;
  color: #606266;
}

.rich-content p {
  margin: 12px 0;
  line-height: 1.8;
  text-align: justify;
}

.rich-content img {
  max-width: 100% !important;
  width: auto !important;
  height: auto !important;
  border-radius: 8px;
  margin: 16px auto;
  display: block;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  object-fit: contain;
}

.rich-content img:hover {
  transform: scale(1.02);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
}

.rich-content ul,
.rich-content ol {
  margin: 12px 0;
  padding-left: 24px;
}

.rich-content li {
  margin: 8px 0;
  line-height: 1.6;
}

.rich-content blockquote {
  margin: 16px 0;
  padding: 16px 20px;
  background: #f0f9ff;
  border-left: 4px solid #409eff;
  border-radius: 0 8px 8px 0;
  font-style: italic;
  color: #606266;
}

.rich-content code {
  background: #f4f4f5;
  padding: 2px 6px;
  border-radius: 4px;
  font-family: 'Monaco', 'Consolas', monospace;
  font-size: 13px;
  color: #e6a23c;
}

.rich-content pre {
  background: #2d3748;
  color: #e2e8f0;
  padding: 16px;
  border-radius: 8px;
  overflow-x: auto;
  margin: 16px 0;
  font-family: 'Monaco', 'Consolas', monospace;
  font-size: 13px;
  line-height: 1.5;
}

.rich-content pre code {
  background: none;
  padding: 0;
  color: inherit;
}

.rich-content a {
  color: #409eff;
  text-decoration: none;
  border-bottom: 1px solid transparent;
  transition: all 0.3s ease;
}

.rich-content a:hover {
  border-bottom-color: #409eff;
  color: #66b1ff;
}

.rich-content strong {
  font-weight: 600;
  color: #303133;
}

.rich-content em {
  font-style: italic;
  color: #606266;
}

.rich-content table {
  width: 100%;
  border-collapse: collapse;
  margin: 16px 0;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.rich-content table th,
.rich-content table td {
  padding: 12px 16px;
  text-align: left;
  border-bottom: 1px solid #ebeef5;
}

.rich-content table th {
  background: #f5f7fa;
  font-weight: 600;
  color: #606266;
}

.rich-content table tr:hover {
  background: #f5f7fa;
}

/* 文本对齐 */
.rich-content .ql-align-center {
  text-align: center;
}

.rich-content .ql-align-right {
  text-align: right;
}

.rich-content .ql-align-justify {
  text-align: justify;
}

/* 字体大小 */
.rich-content .ql-size-small {
  font-size: 12px;
}

.rich-content .ql-size-large {
  font-size: 18px;
}

.rich-content .ql-size-huge {
  font-size: 24px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .main-content {
    padding: 0 20px 20px;
  }
  
  .page-header {
    padding: 20px;
  }
  
  .header-content {
    flex-direction: column;
    gap: 20px;
    align-items: flex-start;
  }
  
  .header-actions {
    flex-wrap: wrap;
    gap: 8px;
  }
  
  .product-info {
    flex-direction: column;
    gap: 20px;
  }
  
  .product-images {
    flex: none;
    align-self: center;
  }
  
  .main-image {
    width: 280px;
    height: 280px;
  }
  
  .stat-card {
    padding: 16px 12px;
  }
  
  .stat-number {
    font-size: 24px;
  }
  
  .specifications {
    grid-template-columns: 1fr;
  }
  
  .rich-content-wrapper {
    padding: 15px;
  }
  
  .rich-content {
    font-size: 13px;
  }
  
  .rich-content h1 {
    font-size: 20px;
  }
  
  .rich-content h2 {
    font-size: 18px;
  }
  
  .rich-content h3 {
    font-size: 16px;
  }
  
  .rich-content img {
    margin: 12px auto;
    max-width: calc(100vw - 80px) !important;
    width: auto !important;
  }
  
  .rich-content pre {
    padding: 12px;
    font-size: 12px;
  }
  
  /* 移动端布局调整 */
  .right-cards-wrapper {
    gap: 16px;
  }
  
  .main-info-card {
    min-height: auto;
  }
  
  .side-card {
    margin-bottom: 0;
  }
  
  /* 移动端取消高度限制 */
  .content .el-row .el-col:first-child,
  .content .el-row .el-col:last-child {
    display: block;
  }
  
  .right-cards-wrapper {
    height: auto !important;
    min-height: auto !important;
  }
  
  .main-info-card {
    min-height: auto !important;
    height: auto !important;
  }
}

/* 图片预览对话框样式 */
:deep(.image-preview-dialog) {
  max-width: 90vw;
}

:deep(.image-preview-dialog .el-message-box__content) {
  padding: 10px;
}

:deep(.image-preview-dialog .el-message-box__message) {
  margin: 0;
}
</style> 